import { useState, useEffect, useCallback } from 'react';
import {
  Search,
  Filter,
  Edit2,
  Trash2,
  Star,
  ChevronDown,
  ChevronUp,
  X,
  Building2,
  Calendar,
  DollarSign,
  TrendingUp,
  BarChart3,
  RefreshCw,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import api from '../../services/api';

// Format currency
const formatCurrency = (value) => {
  if (!value) return '-';
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}B`;
  return `$${Math.round(value)}M`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

// Stats Card Component
function StatsCard({ title, value, icon: Icon, color = 'indigo' }) {
  const colorClasses = {
    indigo: 'bg-indigo-50 text-indigo-600',
    green: 'bg-green-50 text-green-600',
    amber: 'bg-amber-50 text-amber-600',
    blue: 'bg-blue-50 text-blue-600'
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{title}</p>
        </div>
      </div>
    </div>
  );
}

// Edit Modal Component
function EditDealModal({ isOpen, onClose, deal, onSave }) {
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (deal) {
      setFormData({
        mnc_company: deal.mnc_company || '',
        licensor: deal.licensor || '',
        therapeutic_area: deal.therapeutic_area || '',
        modality: deal.modality || '',
        deal_type: deal.deal_type || 'license',
        deal_summary: deal.deal_summary || '',
        deal_date: deal.deal_date ? deal.deal_date.split('T')[0] : '',
        upfront_payment_usd_millions: deal.upfront_payment_usd_millions || '',
        total_value_usd_millions: deal.total_value_usd_millions || '',
        deal_headline: deal.deal_headline || '',
        is_featured: deal.is_featured || false
      });
    }
  }, [deal]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave(deal.id, formData);
      onClose();
    } catch (err) {
      console.error('Save error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit Deal</h2>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Buyer (MNC)</label>
              <input
                type="text"
                value={formData.mnc_company}
                onChange={(e) => setFormData({ ...formData, mnc_company: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Seller (Licensor)</label>
              <input
                type="text"
                value={formData.licensor}
                onChange={(e) => setFormData({ ...formData, licensor: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Therapeutic Area</label>
              <input
                type="text"
                value={formData.therapeutic_area}
                onChange={(e) => setFormData({ ...formData, therapeutic_area: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Modality</label>
              <input
                type="text"
                value={formData.modality}
                onChange={(e) => setFormData({ ...formData, modality: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Deal Date</label>
              <input
                type="date"
                value={formData.deal_date}
                onChange={(e) => setFormData({ ...formData, deal_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Upfront ($M)</label>
              <input
                type="number"
                value={formData.upfront_payment_usd_millions}
                onChange={(e) => setFormData({ ...formData, upfront_payment_usd_millions: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Total Value ($M)</label>
              <input
                type="number"
                value={formData.total_value_usd_millions}
                onChange={(e) => setFormData({ ...formData, total_value_usd_millions: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Headline</label>
            <input
              type="text"
              value={formData.deal_headline}
              onChange={(e) => setFormData({ ...formData, deal_headline: e.target.value })}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deal Summary</label>
            <textarea
              value={formData.deal_summary}
              onChange={(e) => setFormData({ ...formData, deal_summary: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_featured"
              checked={formData.is_featured}
              onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
              className="w-4 h-4 text-indigo-600 border-gray-300 rounded"
            />
            <label htmlFor="is_featured" className="text-sm text-gray-700">Featured Deal</label>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Main Deals Page
export default function AdminDeals() {
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterOptions, setFilterOptions] = useState({ buyers: [], therapeuticAreas: [], years: [] });
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, offset: 0 });

  // Filters
  const [search, setSearch] = useState('');
  const [selectedBuyer, setSelectedBuyer] = useState('');
  const [selectedTA, setSelectedTA] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Edit modal
  const [editingDeal, setEditingDeal] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Fetch deals
  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pagination.limit,
        offset: pagination.offset
      });
      if (search) params.append('search', search);
      if (selectedBuyer) params.append('buyer', selectedBuyer);
      if (selectedTA) params.append('therapeutic_area', selectedTA);
      if (selectedYear) params.append('year', selectedYear);
      if (showFeaturedOnly) params.append('is_featured', 'true');

      const response = await api.get(`/admin/deals?${params}`);
      setDeals(response.data.data);
      setPagination(prev => ({ ...prev, total: response.data.pagination.total }));
    } catch (err) {
      console.error('Failed to fetch deals:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.limit, pagination.offset, search, selectedBuyer, selectedTA, selectedYear, showFeaturedOnly]);

  // Fetch stats
  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/deals/stats');
      setStats(response.data.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  };

  // Fetch filter options
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get('/admin/deals/filters/options');
      setFilterOptions(response.data.data);
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  };

  useEffect(() => {
    fetchDeals();
  }, [fetchDeals]);

  useEffect(() => {
    fetchStats();
    fetchFilterOptions();
  }, []);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({ ...prev, offset: 0 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Toggle featured
  const toggleFeatured = async (deal) => {
    try {
      await api.put(`/admin/deals/${deal.id}/featured`, { is_featured: !deal.is_featured });
      fetchDeals();
      fetchStats();
    } catch (err) {
      console.error('Failed to toggle featured:', err);
    }
  };

  // Delete deal
  const deleteDeal = async (deal) => {
    if (!window.confirm(`Delete deal: ${deal.mnc_company} - ${deal.licensor}?`)) return;
    try {
      await api.delete(`/admin/deals/${deal.id}`);
      fetchDeals();
      fetchStats();
    } catch (err) {
      console.error('Failed to delete deal:', err);
    }
  };

  // Save deal
  const saveDeal = async (id, data) => {
    await api.put(`/admin/deals/${id}`, data);
    fetchDeals();
    fetchStats();
  };

  // Pagination
  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">BD Deals</h1>
          <p className="text-sm text-gray-500">Manage comparable deals data</p>
        </div>
        <button
          onClick={() => { fetchDeals(); fetchStats(); }}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <StatsCard title="Total Deals" value={stats.total} icon={BarChart3} color="indigo" />
          <StatsCard title="Featured" value={stats.featured} icon={Star} color="amber" />
          <StatsCard title="Avg Upfront" value={formatCurrency(stats.valueStats.avgUpfront)} icon={DollarSign} color="green" />
          <StatsCard title="Total Value" value={formatCurrency(stats.valueStats.totalUpfront)} icon={TrendingUp} color="blue" />
        </div>
      )}

      {/* Stats Charts */}
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          {/* By Buyer */}
          <div className="bg-white rounded-xl border p-4">
            <h3 className="font-medium text-gray-900 mb-3">Top Buyers</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {stats.byBuyer.slice(0, 10).map((item, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 truncate flex-1">{item.buyer}</span>
                  <span className="font-medium text-gray-900 ml-2">{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By TA */}
          <div className="bg-white rounded-xl border p-4">
            <h3 className="font-medium text-gray-900 mb-3">By Therapeutic Area</h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {stats.byTherapeuticArea.map((item, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600 truncate flex-1">{item.therapeutic_area || 'Unknown'}</span>
                  <span className="font-medium text-gray-900 ml-2">{item.count}</span>
                </div>
              ))}
            </div>
          </div>

          {/* By Year */}
          <div className="bg-white rounded-xl border p-4">
            <h3 className="font-medium text-gray-900 mb-3">By Year</h3>
            <div className="space-y-2">
              {stats.byYear.map((item, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">{item.year || 'Unknown'}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 bg-gray-100 rounded-full h-2">
                      <div
                        className="bg-indigo-500 h-2 rounded-full"
                        style={{ width: `${(item.count / Math.max(...stats.byYear.map(y => y.count))) * 100}%` }}
                      />
                    </div>
                    <span className="font-medium text-gray-900 w-8 text-right">{item.count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Search & Filters */}
      <div className="bg-white rounded-xl border p-4 mb-4">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search deals..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${showFilters ? 'bg-indigo-50 border-indigo-200 text-indigo-600' : 'text-gray-600'}`}
          >
            <Filter className="w-4 h-4" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-4 gap-4 mt-4 pt-4 border-t">
            <select
              value={selectedBuyer}
              onChange={(e) => { setSelectedBuyer(e.target.value); setPagination(p => ({ ...p, offset: 0 })); }}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Buyers</option>
              {filterOptions.buyers.map(b => (
                <option key={b} value={b}>{b}</option>
              ))}
            </select>

            <select
              value={selectedTA}
              onChange={(e) => { setSelectedTA(e.target.value); setPagination(p => ({ ...p, offset: 0 })); }}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Therapeutic Areas</option>
              {filterOptions.therapeuticAreas.map(ta => (
                <option key={ta} value={ta}>{ta}</option>
              ))}
            </select>

            <select
              value={selectedYear}
              onChange={(e) => { setSelectedYear(e.target.value); setPagination(p => ({ ...p, offset: 0 })); }}
              className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">All Years</option>
              {filterOptions.years.map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showFeaturedOnly}
                onChange={(e) => { setShowFeaturedOnly(e.target.checked); setPagination(p => ({ ...p, offset: 0 })); }}
                className="w-4 h-4 text-indigo-600 rounded"
              />
              <span className="text-sm text-gray-600">Featured only</span>
            </label>
          </div>
        )}
      </div>

      {/* Deals Table */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Buyer</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Seller</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">TA</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Upfront</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-gray-500 uppercase">Total</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Featured</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : deals.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-gray-500">No deals found</td>
                </tr>
              ) : (
                deals.map((deal) => (
                  <tr key={deal.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-gray-400" />
                        <span className="font-medium text-gray-900 truncate max-w-[150px]">{deal.mnc_company}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-gray-600 truncate max-w-[150px] block">{deal.licensor}</span>
                    </td>
                    <td className="px-4 py-3">
                      {deal.therapeutic_area && (
                        <span className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs rounded-full">
                          {deal.therapeutic_area}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 text-sm text-gray-500">
                        <Calendar className="w-3 h-3" />
                        {formatDate(deal.deal_date)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-medium text-green-600">
                        {formatCurrency(deal.upfront_payment_usd_millions)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className="text-gray-600">
                        {formatCurrency(deal.total_value_usd_millions)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => toggleFeatured(deal)}
                        className={`p-1 rounded ${deal.is_featured ? 'text-amber-500' : 'text-gray-300 hover:text-amber-400'}`}
                      >
                        <Star className="w-5 h-5" fill={deal.is_featured ? 'currentColor' : 'none'} />
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => { setEditingDeal(deal); setShowEditModal(true); }}
                          className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded"
                          title="Edit"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deleteDeal(deal)}
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {pagination.offset + 1} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPagination(p => ({ ...p, offset: Math.max(0, p.offset - p.limit) }))}
              disabled={pagination.offset === 0}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setPagination(p => ({ ...p, offset: p.offset + p.limit }))}
              disabled={pagination.offset + pagination.limit >= pagination.total}
              className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      <EditDealModal
        isOpen={showEditModal}
        onClose={() => { setShowEditModal(false); setEditingDeal(null); }}
        deal={editingDeal}
        onSave={saveDeal}
      />
    </div>
  );
}
